import * as d3 from "d3";
import { getTheme } from "@semantical/theme.ts";
import React, {MouseEvent} from "react";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import DiagramEditor from "@semantical/components/diagram-editor/DiagramEditor.tsx";
import { SchemaClass, computeRestrictedObjectProperties, ObjectProperty } from "@semantical/modules/ontology-model";
import { Individual } from "@semantical/modules/knowledge-graph";
import type { Node } from "@semantical/modules/semantical-core";
import { isIndividual } from "@semantical/modules/semantical-core";
import Tooltips from "./Tooltips.tsx";
import { SvgNamespaceTag } from "@semantical/components/toolbox/SvgNamespaceTag";

function getTranslateValues(transformString: string) {
    // Extracts the translate(x,y) values from the transform string
    const match = /translate\(([^,]+),([^,]+)\)/.exec(transformString);
    return match ? {x: parseFloat(match[1]), y: parseFloat(match[2])} : {x: 0, y: 0};
}

/**
 * Convert a color (rgba/rgb/hex) to an opaque RGB color.
 * - Ignores alpha channel, uses RGB values directly
 * - Optionally ensures minimum luminance for readable black text
 */
function ensureOpaqueReadableColor(color: string): string {
    let r: number, g: number, b: number;

    // Parse rgba (ignore alpha, use RGB values directly)
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbaMatch) {
        r = parseInt(rgbaMatch[1]);
        g = parseInt(rgbaMatch[2]);
        b = parseInt(rgbaMatch[3]);
    } else if (color.startsWith('#')) {
        // Parse hex
        const hex = color.slice(1);
        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
    } else {
        // Return as-is if can't parse
        return color;
    }

    // Ignore alpha - use the RGB values directly for opaque color

    // Calculate relative luminance (WCAG formula)
    const toLinear = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    // Minimum luminance for readable black text (0.25 provides ~5.5:1 contrast ratio)
    const minLuminance = 0.15;

    if (luminance < minLuminance) {
        // Lighten the color by blending with white
        // Find blend factor t such that new luminance >= minLuminance
        // Approximate: blend more with white until luminance is sufficient
        let t = 0;
        let newR = r, newG = g, newB = b;
        while (t < 1) {
            t += 0.05;
            newR = Math.round(r + (255 - r) * t);
            newG = Math.round(g + (255 - g) * t);
            newB = Math.round(b + (255 - b) * t);
            const newLum = 0.2126 * toLinear(newR) + 0.7152 * toLinear(newG) + 0.0722 * toLinear(newB);
            if (newLum >= minLuminance) break;
        }
        r = newR;
        g = newG;
        b = newB;
    }

    return `rgb(${r}, ${g}, ${b})`;
}

type Props = {
    diagramClass: DiagramClass
    diagramEditor: DiagramEditor
    // Whether this class is currently selected (from App state)
    isSelected?: boolean
    // Whether this class is being hovered as a reconnection target
    reconnectHighlight?: boolean
    // Whether to show optional properties (cardinality min = 0)
    showOptionalProperties?: boolean
    // Whether to show essential properties (object property restrictions with cardinality min >= 1)
    showEssentialProperties?: boolean
    // Whether to show the stereotype (e.g., «Kind», «Role»)
    showStereotype?: boolean
    // Callback for adding relationships (for Tooltips)
    onAddRelationship?: (predicate: ObjectProperty, source: DiagramClass, target: DiagramClass) => void
    // Callback for showing relationship picker (for Tooltips)
    onShowRelationshipPicker?: (source: DiagramClass, target: DiagramClass, position: {x: number, y: number}) => void
    // Optional resolver for class category colors (used for Individuals with classUris)
    getClassColor?: (classUri: string) => string | undefined
    // Optional resolver for class labels (used for SchemaIndividual stereotypes)
    getClassLabel?: (classUri: string) => string | undefined
    // Optional resolver for namespace tag info (prefix and color) by source graph IRI
    getNamespaceTag?: (sourceGraphIRI: string) => { prefix?: string; color?: string } | undefined
    // Dynamic resolver for data properties by class URI (from current ontology)
    getDataPropertiesForClass?: (classUri: string) => import("@semantical/modules/ontology-model").DataProperty[] | undefined
    // Dynamic resolver for object property restrictions by class URI (from current ontology)
    getObjectPropertyRestrictionsForClass?: (classUri: string) => import("@semantical/modules/ontology-model").OwlClassExpression[] | undefined
}

type State = {}

export default class DiagramClassElement extends React.Component<Props, State> {

    svgClass: React.RefObject<SVGGElement | null>;

    handleMouseOver = (_e: MouseEvent) => this.highlight(true);

    handleMouseOut = (_e: MouseEvent) => {
        // Only remove highlight if this element is not selected
        if (!this.props.isSelected) {
            this.highlight(false);
        }
    }

    handleMouseClick = (_e: MouseEvent) => {
        console.debug("Clicked on target: " + this.props.diagramClass.modelElement.name);

        // Interrupt any ongoing transitions to ensure click is processed immediately
        d3.select(this.svgClass.current).selectAll("*").interrupt();

        // Notify parent via callback - selection state managed by App
        this.props.diagramEditor.props.onClassSelected(this.props.diagramClass);
    }

    constructor(props: Props) {
        super(props);

        this.svgClass = React.createRef<SVGGElement>();
    }

    /**
     * Format a data property for display in UML notation
     * e.g., "hasDateOfBirth : date [1]" or "hasName : string [0..1]"
     */
    formatDataProperty(prop: { uri?: string; localName: string; label: string; range: string; cardinality?: { min?: number; max?: number; exact?: number } }): string {
        let cardinality = '';
        if (prop.cardinality) {
            if (prop.cardinality.exact !== undefined) {
                // Hide [1] as it's the default expected cardinality
                if (prop.cardinality.exact !== 1) {
                    cardinality = ` [${prop.cardinality.exact}]`;
                }
            } else if (prop.cardinality.min !== undefined || prop.cardinality.max !== undefined) {
                const min = prop.cardinality.min ?? 0;
                const max = prop.cardinality.max !== undefined ? prop.cardinality.max : '*';
                // Hide [1..1] as it's the default expected cardinality
                if (!(min === 1 && max === 1)) {
                    cardinality = ` [${min}..${max}]`;
                }
            }
        } else {
            // No cardinality specified = optional with no upper bound
            cardinality = ' [0..*]';
        }

        // Check if the model element is an individual with a value for this property
        const modelElement = this.props.diagramClass.modelElement;
        let valueStr = '';
        if (modelElement instanceof Individual && prop.uri) {
            const value = modelElement.getPropertyValue(prop.uri);
            if (value !== undefined) {
                valueStr = ` = "${value}"`;
            }
        }

        return `${prop.localName} : ${prop.range}${cardinality}${valueStr}`;
    }

    /**
     * Format an object property restriction (existential axiom) for display in UML notation
     * e.g., "hasChild : Child [1..*]" or "hasNationality : Country = Belgium" (for hasValue)
     */
    formatObjectPropertyRestriction(restriction: {
        localName: string;
        targetClassLabel: string;
        cardinality: { min: number; max: number | '*' };
        hasValue?: string;
        hasValueLabel?: string;
    }): string {
        // For hasValue restrictions, show range type and fixed individual value
        if (restriction.hasValue) {
            // Format: propertyName : RangeType = IndividualValue
            const valueLabel = restriction.hasValueLabel || restriction.hasValue;
            return `${restriction.localName} : ${restriction.targetClassLabel} = ${valueLabel}`;
        }
        // For other restrictions, show cardinality with colon (hide [1..1] as default)
        const { min, max } = restriction.cardinality;
        if (min === 1 && max === 1) {
            return `${restriction.localName} : ${restriction.targetClassLabel}`;
        }
        const cardinality = min === max ? `[${min}]` : `[${min}..${max}]`;
        return `${restriction.localName} : ${restriction.targetClassLabel} ${cardinality}`;
    }

    /**
     * Check if a property is required (must be displayed even without a value)
     */
    isRequiredProperty(prop: { cardinality?: { min?: number; max?: number; exact?: number } }): boolean {
        if (!prop.cardinality) return false;
        // Required if exact cardinality >= 1 or minCardinality >= 1
        if (prop.cardinality.exact !== undefined && prop.cardinality.exact >= 1) return true;
        if (prop.cardinality.min !== undefined && prop.cardinality.min >= 1) return true;
        return false;
    }

    /**
     * Check if a property is optional.
     * Optional properties are those with:
     * - No cardinality specified (domain-only properties are semantically optional)
     * - Explicit min cardinality of 0
     * - Exact cardinality of 0 (edge case)
     */
    isOptionalProperty(prop: { cardinality?: { min?: number; max?: number; exact?: number } }): boolean {
        // No cardinality = optional (domain-only property)
        if (!prop.cardinality) return true;
        // Explicit exact = 0
        if (prop.cardinality.exact !== undefined && prop.cardinality.exact === 0) return true;
        // Explicit min = 0 (and not required via exact >= 1)
        if (prop.cardinality.min !== undefined && prop.cardinality.min === 0) return true;
        // If only max is set (no min), it's implicitly optional
        if (prop.cardinality.min === undefined && prop.cardinality.exact === undefined) return true;
        return false;
    }

    /**
     * Check if an object property restriction is required.
     * Required if min cardinality >= 1
     */
    isRequiredObjectPropertyRestriction(restriction: { cardinality: { min: number; max: number | '*' } }): boolean {
        return restriction.cardinality.min >= 1;
    }

    /**
     * Check if an object property restriction is optional.
     * Optional if min cardinality === 0
     */
    isOptionalObjectPropertyRestriction(restriction: { cardinality: { min: number; max: number | '*' } }): boolean {
        return restriction.cardinality.min === 0;
    }

    /**
     * Estimate text width in pixels based on character count and font size.
     * Uses approximate character widths for Arial font.
     */
    static estimateTextWidth(text: string, fontSize: number): number {
        // Average character width ratio for Arial (varies by character, ~0.5-0.6 of font size)
        const avgCharWidthRatio = 0.6;
        return text.length * fontSize * avgCharWidthRatio;
    }

    /**
     * Get data properties for a model element (works for both SchemaClass and Individual)
     * For Individuals, uses dynamic resolver if provided to look up properties from current ontology.
     * @param modelElement The model element to get data properties for
     * @param dataPropertiesResolver Optional resolver function to look up data properties by class URI (for dynamic ontology switching)
     */
    static getDataProperties(
        modelElement: Node,
        dataPropertiesResolver?: (classUri: string) => import("@semantical/modules/ontology-model").DataProperty[] | undefined
    ) {
        if (modelElement instanceof Individual) {
            // For Individuals, prefer dynamic lookup from current ontology if resolver is provided
            if (dataPropertiesResolver && modelElement.modelClass?.uri) {
                const dynamicProps = dataPropertiesResolver(modelElement.modelClass.uri);
                if (dynamicProps !== undefined) {
                    return dynamicProps;
                }
            }
            // Fall back to stored properties on modelClass
            return modelElement.modelClass.dataProperties;
        } else if (modelElement instanceof SchemaClass) {
            // For SchemaClass (TBox), use stored properties directly
            return modelElement.dataProperties;
        }
        return undefined;
    }

    /**
     * Get subClassOf axioms for a model element (for computing object property restrictions)
     * For Individuals, uses dynamic resolver if provided to look up restrictions from current ontology.
     * @param modelElement The model element to get subClassOf axioms for
     * @param objectPropertyRestrictionsResolver Optional resolver function to look up restrictions by class URI (for dynamic ontology switching)
     */
    static getSubClassOf(
        modelElement: Node,
        objectPropertyRestrictionsResolver?: (classUri: string) => import("@semantical/modules/ontology-model").OwlClassExpression[] | undefined
    ) {
        if (modelElement instanceof Individual) {
            // For Individuals, prefer dynamic lookup from current ontology if resolver is provided
            if (objectPropertyRestrictionsResolver && modelElement.modelClass?.uri) {
                const dynamicRestrictions = objectPropertyRestrictionsResolver(modelElement.modelClass.uri);
                if (dynamicRestrictions !== undefined) {
                    return dynamicRestrictions;
                }
            }
            // Fall back to stored subClassOf on modelClass
            return modelElement.modelClass._subClassOf;
        } else if (modelElement instanceof SchemaClass) {
            // For SchemaClass (TBox), use stored axioms directly
            return modelElement._subClassOf;
        }
        return undefined;
    }

    /**
     * Get category color for a model element.
     * For Individual interface (both ABox and TBox): use classColorResolver with first classUri
     * For SchemaClass: use categoryColor directly
     */
    static getCategoryColor(modelElement: Node, classColorResolver?: (classUri: string) => string | undefined): string | undefined {
        // Check for Individual interface (both ABox Individual and TBox SchemaIndividual)
        if (isIndividual(modelElement) && classColorResolver) {
            const classUri = modelElement.classUris[0];
            if (classUri) {
                return classColorResolver(classUri);
            }
        }
        // Check for SchemaClass
        if (modelElement instanceof SchemaClass) {
            return modelElement.categoryColor;
        }
        return undefined;
    }

    /**
     * Get the stereotype display text for a model element
     */
    static getStereotype(modelElement: Node, classLabelResolver?: (classUri: string) => string | undefined): string {
        if (modelElement instanceof Individual) {
            // For individuals (ABox), use the class label as stereotype
            return `«${modelElement.modelClass.label || modelElement.modelClass.name}»`;
        } else if (modelElement instanceof SchemaClass) {
            // For classes (TBox), use sem:stereotype or type
            return `«${modelElement.stereotype || modelElement.type}»`;
        } else if (isIndividual(modelElement)) {
            // For SchemaIndividual (TBox individual), resolve class label from classUris
            const classUri = modelElement.classUris[0];
            if (classUri && classLabelResolver) {
                const classLabel = classLabelResolver(classUri);
                if (classLabel) {
                    return `«${classLabel}»`;
                }
            }
            // No class available yet (ontology not loaded) - return empty string
            // The stereotype will appear once the ontology loads and diagram re-renders
            return '';
        }
        return `«unknown»`;
    }

    /**
     * Get a short prefix for the source graph IRI (for badge display)
     */
    static getSourceGraphPrefix(ontologyIRI: string): string {
        // Try to extract meaningful prefix from the IRI
        const hashIndex = ontologyIRI.lastIndexOf('#');
        const slashIndex = ontologyIRI.lastIndexOf('/');
        const separatorIndex = Math.max(hashIndex, slashIndex);
        if (separatorIndex !== -1 && separatorIndex < ontologyIRI.length - 1) {
            const localPart = ontologyIRI.substring(separatorIndex + 1);
            // Return up to 4 characters, uppercase
            return localPart.substring(0, 4).toUpperCase();
        }
        // Fallback: use first 4 characters of full IRI
        return ontologyIRI.substring(0, 4).toUpperCase();
    }

    /**
     * Get a consistent color for a source graph IRI (hash-based)
     */
    static getSourceGraphColor(ontologyIRI: string): string {
        const colors = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
        let hash = 0;
        for (let i = 0; i < ontologyIRI.length; i++) {
            hash = ((hash * 31) + ontologyIRI.charCodeAt(i)) | 0; // |0 keeps it as 32-bit integer
        }
        return colors[Math.abs(hash) % colors.length];
    }

    /**
     * Get the instance properties for a model element
     */
    static getInstanceProperties(modelElement: Node): string[] {
        if (modelElement instanceof SchemaClass) {
            return modelElement.properties;
        }
        // Individual doesn't have instance properties yet
        return [];
    }

    /**
     * Check if a property is required (static version for calculateDimensions)
     */
    static isRequiredPropertyStatic(prop: { cardinality?: { min?: number; max?: number; exact?: number } }): boolean {
        if (!prop.cardinality) return false;
        if (prop.cardinality.exact !== undefined && prop.cardinality.exact >= 1) return true;
        if (prop.cardinality.min !== undefined && prop.cardinality.min >= 1) return true;
        return false;
    }

    /**
     * Check if a property is optional (static version for calculateDimensions)
     */
    static isOptionalPropertyStatic(prop: { cardinality?: { min?: number; max?: number; exact?: number } }): boolean {
        if (!prop.cardinality) return true;
        if (prop.cardinality.exact !== undefined && prop.cardinality.exact === 0) return true;
        if (prop.cardinality.min !== undefined && prop.cardinality.min === 0) return true;
        if (prop.cardinality.min === undefined && prop.cardinality.exact === undefined) return true;
        return false;
    }

    /**
     * Check if an object property restriction is required (static version)
     */
    static isRequiredObjectPropertyRestrictionStatic(restriction: { cardinality: { min: number; max: number | '*' } }): boolean {
        return restriction.cardinality.min >= 1;
    }

    /**
     * Check if an object property restriction is optional (static version)
     */
    static isOptionalObjectPropertyRestrictionStatic(restriction: { cardinality: { min: number; max: number | '*' } }): boolean {
        return restriction.cardinality.min === 0;
    }

    /**
     * Calculate the dimensions for a Node based on its content.
     * This is a static method so it can be used before the component renders.
     * @param modelElement The model element to calculate dimensions for
     * @param showOptionalProperties Whether to include optional properties in dimension calculation (default: true)
     * @param showEssentialProperties Whether to include essential properties in dimension calculation (default: true)
     * @param classLabelResolver Optional resolver for class labels (for SchemaIndividual stereotypes)
     * @param namespaceTagPrefix Optional namespace tag prefix (adds width for the tag badge)
     * @param dataPropertiesResolver Optional resolver for dynamic data property lookup
     * @param objectPropertyRestrictionsResolver Optional resolver for dynamic object property restrictions lookup
     */
    static calculateDimensions(
        modelElement: Node,
        showOptionalProperties: boolean = true,
        showEssentialProperties: boolean = true,
        classLabelResolver?: (classUri: string) => string | undefined,
        namespaceTagPrefix?: string,
        dataPropertiesResolver?: (classUri: string) => import("@semantical/modules/ontology-model").DataProperty[] | undefined,
        objectPropertyRestrictionsResolver?: (classUri: string) => import("@semantical/modules/ontology-model").OwlClassExpression[] | undefined
    ): { width: number; height: number } {
        const MIN_WIDTH = 150;
        const PADDING = 20;
        const SEPARATOR_HEIGHT = 12; // Space for separator line + padding above and below

        // Get ontology data properties - separate required from optional
        // Use dynamic resolver if provided (for ontology switching), otherwise use stored properties
        const allOntologyProps = DiagramClassElement.getDataProperties(modelElement, dataPropertiesResolver) || [];
        const requiredOntologyPropsAll = allOntologyProps.filter(prop => DiagramClassElement.isRequiredPropertyStatic(prop));
        const optionalOntologyPropsAll = allOntologyProps.filter(prop =>
            DiagramClassElement.isOptionalPropertyStatic(prop) && !DiagramClassElement.isRequiredPropertyStatic(prop)
        );

        // Get object property restrictions - separate required from optional
        // Use dynamic resolver if provided (for ontology switching), otherwise use stored axioms
        const subClassOf = DiagramClassElement.getSubClassOf(modelElement, objectPropertyRestrictionsResolver);
        const allObjectPropertyRestrictions = subClassOf ? computeRestrictedObjectProperties({ subClassOf }) : [];
        const requiredObjectPropertyRestrictionsAll = allObjectPropertyRestrictions.filter(r =>
            DiagramClassElement.isRequiredObjectPropertyRestrictionStatic(r)
        );
        const optionalObjectPropertyRestrictionsAll = allObjectPropertyRestrictions.filter(r =>
            DiagramClassElement.isOptionalObjectPropertyRestrictionStatic(r)
        );

        // Apply visibility filters
        // "Essential" refers to required properties (data properties and object property restrictions with cardinality >= 1)
        const requiredOntologyProps = showEssentialProperties ? requiredOntologyPropsAll : [];
        const optionalOntologyProps = showOptionalProperties ? optionalOntologyPropsAll : [];
        const requiredObjectPropertyRestrictions = showEssentialProperties ? requiredObjectPropertyRestrictionsAll : [];
        const optionalObjectPropertyRestrictions = showOptionalProperties ? optionalObjectPropertyRestrictionsAll : [];

        // Get instance properties
        const instanceProps = DiagramClassElement.getInstanceProperties(modelElement);

        // Calculate height - include separator if we have optional properties
        const requiredPropsCount = requiredOntologyProps.length + requiredObjectPropertyRestrictions.length + instanceProps.length;
        const optionalPropsCount = optionalOntologyProps.length + optionalObjectPropertyRestrictions.length;
        const allPropsCount = requiredPropsCount + optionalPropsCount;

        // Show properties section if:
        // 1. There are visible properties, OR
        // 2. Optional or essential properties toggle is enabled (for consistent UX)
        const hasVisibleProperties = allPropsCount > 0;
        const showPropertiesSection = hasVisibleProperties || showOptionalProperties || showEssentialProperties;
        const hasSeparator = hasVisibleProperties && optionalPropsCount > 0 && requiredPropsCount > 0;
        const separatorSpace = hasSeparator ? SEPARATOR_HEIGHT : 0;
        // Minimum height of 40 for empty properties section when toggles are on
        const propsHeight = showPropertiesSection ? Math.max(40, allPropsCount * 16 + 20 + separatorSpace) : 0;
        const height = 40 + propsHeight;

        // Calculate width
        const stereotype = DiagramClassElement.getStereotype(modelElement, classLabelResolver);
        const className = modelElement.name;

        // Format properties for width calculation
        const formatDataProperty = (prop: any): string => {
            let cardinality = '';
            if (prop.cardinality) {
                if (prop.cardinality.exact !== undefined) {
                    // Hide [1] as it's the default expected cardinality
                    if (prop.cardinality.exact !== 1) {
                        cardinality = ` [${prop.cardinality.exact}]`;
                    }
                } else if (prop.cardinality.min !== undefined || prop.cardinality.max !== undefined) {
                    const min = prop.cardinality.min ?? 0;
                    const max = prop.cardinality.max !== undefined ? prop.cardinality.max : '*';
                    // Hide [1..1] as it's the default expected cardinality
                    if (!(min === 1 && max === 1)) {
                        cardinality = ` [${min}..${max}]`;
                    }
                }
            } else {
                // No cardinality specified = optional with no upper bound
                cardinality = ' [0..*]';
            }

            // Check if the model element is an individual with a value for this property
            let valueStr = '';
            if (modelElement instanceof Individual && prop.uri) {
                const value = modelElement.getPropertyValue(prop.uri);
                if (value !== undefined) {
                    valueStr = ` = "${value}"`;
                }
            }

            return `${prop.localName} : ${prop.range}${cardinality}${valueStr}`;
        };

        const formatRestriction = (r: any): string => {
            if (r.hasValue) {
                return `${r.localName} : ${r.targetClassLabel} = ${r.hasValue.individualLabel}`;
            }
            const { min, max } = r.cardinality;
            // Hide [1..1] as it's the default expected cardinality
            if (min === 1 && max === 1) {
                return `${r.localName} : ${r.targetClassLabel}`;
            }
            const cardinality = min === max ? `[${min}]` : `[${min}..${max}]`;
            return `${r.localName} : ${r.targetClassLabel} ${cardinality}`;
        };

        const formattedRequiredOntologyProps = requiredOntologyProps.map(formatDataProperty);
        const formattedOptionalOntologyProps = optionalOntologyProps.map(formatDataProperty);
        const formattedRequiredRestrictions = requiredObjectPropertyRestrictions.map(formatRestriction);
        const formattedOptionalRestrictions = optionalObjectPropertyRestrictions.map(formatRestriction);

        const stereotypeWidth = DiagramClassElement.estimateTextWidth(stereotype, 12);
        const classNameWidth = DiagramClassElement.estimateTextWidth(className, 16);
        const requiredOntologyPropsMaxWidth = formattedRequiredOntologyProps.length > 0
            ? Math.max(...formattedRequiredOntologyProps.map(p => DiagramClassElement.estimateTextWidth(p, 12)))
            : 0;
        const optionalOntologyPropsMaxWidth = formattedOptionalOntologyProps.length > 0
            ? Math.max(...formattedOptionalOntologyProps.map(p => DiagramClassElement.estimateTextWidth(p, 12)))
            : 0;
        const requiredRestrictionsMaxWidth = formattedRequiredRestrictions.length > 0
            ? Math.max(...formattedRequiredRestrictions.map(p => DiagramClassElement.estimateTextWidth(p, 12)))
            : 0;
        const optionalRestrictionsMaxWidth = formattedOptionalRestrictions.length > 0
            ? Math.max(...formattedOptionalRestrictions.map(p => DiagramClassElement.estimateTextWidth(p, 12)))
            : 0;
        const instancePropsMaxWidth = instanceProps.length > 0
            ? Math.max(...instanceProps.map(p => DiagramClassElement.estimateTextWidth(p, 12)))
            : 0;

        // Calculate namespace tag width if present (badge width + margin)
        const namespaceTagWidth = namespaceTagPrefix
            ? Math.max(26, namespaceTagPrefix.length * 6 + 10) + 8  // badge width + margin
            : 0;

        const contentWidth = Math.max(
            stereotypeWidth + namespaceTagWidth,  // stereotype row needs space for tag
            classNameWidth + namespaceTagWidth,   // class name row needs space for tag
            requiredOntologyPropsMaxWidth + PADDING,
            optionalOntologyPropsMaxWidth + PADDING,
            requiredRestrictionsMaxWidth + PADDING,
            optionalRestrictionsMaxWidth + PADDING,
            instancePropsMaxWidth + PADDING
        );
        const width = Math.max(MIN_WIDTH, contentWidth + PADDING);

        return { width, height };
    }

    render() {
        const theme = getTheme();
        const modelElement = this.props.diagramClass.modelElement;
        const showOptionalProperties = this.props.showOptionalProperties ?? true;
        const showEssentialProperties = this.props.showEssentialProperties ?? true;
        console.debug("Rendering target: " + modelElement.name);

        // Get ontology data properties - separate required from optional
        // Use dynamic resolvers to look up properties from current ontology (for ontology switching)
        const allOntologyProps = DiagramClassElement.getDataProperties(modelElement, this.props.getDataPropertiesForClass) || [];
        const requiredOntologyPropsAll = allOntologyProps.filter(prop => this.isRequiredProperty(prop));
        const optionalOntologyPropsAll = allOntologyProps.filter(prop =>
            this.isOptionalProperty(prop) && !this.isRequiredProperty(prop)
        );

        // Get object property restrictions - separate required from optional
        // Use dynamic resolvers to look up restrictions from current ontology (for ontology switching)
        const subClassOf = DiagramClassElement.getSubClassOf(modelElement, this.props.getObjectPropertyRestrictionsForClass);
        const allObjectPropertyRestrictions = subClassOf ? computeRestrictedObjectProperties({ subClassOf }) : [];
        const requiredObjectPropertyRestrictionsAll = allObjectPropertyRestrictions.filter(r =>
            this.isRequiredObjectPropertyRestriction(r)
        );
        const optionalObjectPropertyRestrictionsAll = allObjectPropertyRestrictions.filter(r =>
            this.isOptionalObjectPropertyRestriction(r)
        );

        // Apply visibility filters
        // "Essential" refers to required properties (data properties and object property restrictions with cardinality >= 1)
        const requiredOntologyProps = showEssentialProperties ? requiredOntologyPropsAll : [];
        const optionalOntologyProps = showOptionalProperties ? optionalOntologyPropsAll : [];
        const requiredObjectPropertyRestrictions = showEssentialProperties ? requiredObjectPropertyRestrictionsAll : [];
        const optionalObjectPropertyRestrictions = showOptionalProperties ? optionalObjectPropertyRestrictionsAll : [];

        // Get instance properties (user-defined)
        const instanceProps = DiagramClassElement.getInstanceProperties(modelElement);

        // Get namespace tag prefix for width calculation
        const sourceGraphIRI = this.props.diagramClass.sourceGraphIRI;
        const namespaceTag = sourceGraphIRI ? this.props.getNamespaceTag?.(sourceGraphIRI) : undefined;

        // Calculate dimensions using static method (also updates the DiagramClass model)
        // Pass dynamic resolvers for property lookup (enables ontology switching)
        const { width, height: totalHeight } = DiagramClassElement.calculateDimensions(
            modelElement,
            showOptionalProperties,
            showEssentialProperties,
            this.props.getClassLabel,
            namespaceTag?.prefix,
            this.props.getDataPropertiesForClass,
            this.props.getObjectPropertyRestrictionsForClass
        );

        // Update the DiagramClass model with calculated dimensions
        // This ensures relationships use correct anchor positions
        this.props.diagramClass.width = width;
        this.props.diagramClass.height = totalHeight;

        // Calculate props section height for rendering
        const SEPARATOR_HEIGHT = 12; // Space for separator line + padding above and below
        const requiredPropsCount = requiredOntologyProps.length + requiredObjectPropertyRestrictions.length + instanceProps.length;
        const optionalPropsCount = optionalOntologyProps.length + optionalObjectPropertyRestrictions.length;
        const allPropsCount = requiredPropsCount + optionalPropsCount;

        // Show properties section if:
        // 1. There are visible properties, OR
        // 2. Optional or essential properties toggle is enabled (for consistent UX)
        const hasVisibleProperties = allPropsCount > 0;
        const showPropertiesSection = hasVisibleProperties || showOptionalProperties || showEssentialProperties;
        const hasSeparator = hasVisibleProperties && optionalPropsCount > 0 && requiredPropsCount > 0;
        const separatorSpace = hasSeparator ? SEPARATOR_HEIGHT : 0;
        // Minimum height of 40 for empty properties section when toggles are on
        const propsHeight = showPropertiesSection ? Math.max(40, allPropsCount * 16 + 20 + separatorSpace) : 0;

        // Get display text
        const showStereotype = this.props.showStereotype ?? true;
        const stereotype = DiagramClassElement.getStereotype(modelElement, this.props.getClassLabel);
        const className = modelElement.name;

        // Get categoryColor for title background when stereotypes are hidden
        const categoryColor = DiagramClassElement.getCategoryColor(modelElement, this.props.getClassColor);

        // Determine if we're in "graph node" mode (minimal display with rounded corners)
        const isGraphNodeMode = !showStereotype && !showOptionalProperties && !showEssentialProperties;
        const cornerRadius = isGraphNodeMode ? 8 : 0;

        // Calculate Y positions for property rendering
        const baseY = 40 + 4 + 12; // Title height + padding
        // separatorY is the Y position of the last required property's baseline + one line height
        const separatorY = baseY + (requiredPropsCount * 16) - 10; // -10 to position line between rows
        // Optional properties Y position: after separator if there's one, otherwise at baseY
        const optionalBaseY = hasSeparator ? baseY + (requiredPropsCount * 16) + separatorSpace : baseY;

        return (
            <g ref={this.svgClass} className="diagramClass" transform={`translate(${this.props.diagramClass.x}, ${this.props.diagramClass.y})`} data-diagram-class-name={this.props.diagramClass.modelElement.name}>
                {/* Visual elements - don't capture events */}
                <rect className="wc" width={width} height={totalHeight} rx={cornerRadius} ry={cornerRadius}
                      style={{strokeWidth: 1, stroke: theme.classBorder, fill: 'none', pointerEvents: 'none'}}/>
                <rect className="wchover" width={width} height={totalHeight} rx={cornerRadius} ry={cornerRadius}
                      style={{strokeWidth: 0, stroke: theme.classBorderHover, strokeOpacity: 0.5, fill: 'none', pointerEvents: 'none'}}/>
                <rect className="title" width={width} height={isGraphNodeMode ? totalHeight : 40} rx={cornerRadius} ry={cornerRadius}
                      style={{fill: (!showStereotype && categoryColor) ? ensureOpaqueReadableColor(categoryColor) : theme.classTitleBackground, pointerEvents: 'none'}}/>
                {showPropertiesSection && (
                    <rect className="props" y={40} width={width} height={propsHeight} style={{fill: theme.classPropsBackground, pointerEvents: 'none'}}/>
                )}
                {showStereotype && (
                    <text x={width / 2} y={15}
                          style={{textAnchor: 'middle', dominantBaseline: 'middle', fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none'}}>
                        {stereotype}
                    </text>
                )}
                <text x={width / 2} y={showStereotype ? 30 : 20}
                      style={{textAnchor: 'middle', dominantBaseline: 'middle', fontFamily: 'Arial', fontSize: '16px', pointerEvents: 'none'}}>
                    {className}
                </text>
                {/* Source graph badge - shown for TBox individuals from loaded ontologies */}
                {this.props.diagramClass.sourceGraphIRI && (() => {
                    const sourceGraphIRI = this.props.diagramClass.sourceGraphIRI!;
                    // Try to get namespace tag from workspace resolver
                    const namespaceTag = this.props.getNamespaceTag?.(sourceGraphIRI);
                    // Use namespace prefix if available, otherwise fall back to static method
                    const prefix = namespaceTag?.prefix || DiagramClassElement.getSourceGraphPrefix(sourceGraphIRI);
                    // Use namespace color if set, otherwise let SvgNamespaceTag use default violet
                    const color = namespaceTag?.color;
                    return (
                        <SvgNamespaceTag
                            prefix={prefix}
                            color={color}
                            x={width - 2}
                            y={2}
                        />
                    );
                })()}
                {/* Required ontology data properties (from OWL class) - in blue */}
                {requiredOntologyProps.map((prop, index) => (
                    <text key={`onto-${index}`} className="property ontology-property" x={10} y={baseY + (index * 16)}
                          style={{fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none', fill: '#3366aa'}}>
                        {this.formatDataProperty(prop)}
                    </text>
                ))}
                {/* Required object property restrictions (existential axioms) - in orange */}
                {requiredObjectPropertyRestrictions.map((restriction, index) => (
                    <text key={`axiom-${index}`} className="property axiom-property" x={10} y={baseY + ((requiredOntologyProps.length + index) * 16)}
                          style={{fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none', fill: '#aa6633'}}>
                        {this.formatObjectPropertyRestriction(restriction)}
                    </text>
                ))}
                {/* Instance properties (user-defined) */}
                {instanceProps.map((prop, index) => (
                    <text key={`inst-${index}`} className="property instance-property" x={10} y={baseY + ((requiredOntologyProps.length + requiredObjectPropertyRestrictions.length + index) * 16)}
                          style={{fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none'}}>
                        {prop}
                    </text>
                ))}
                {/* Separator line between required and optional properties */}
                {hasSeparator && (
                    <line className="separator" x1={5} y1={separatorY + 2} x2={width - 5} y2={separatorY + 2}
                          style={{stroke: '#999999', strokeWidth: 1, strokeDasharray: '3,2', pointerEvents: 'none'}}/>
                )}
                {/* Optional ontology data properties - in lighter blue */}
                {optionalOntologyProps.map((prop, index) => (
                    <text key={`opt-data-${index}`} className="property optional-property" x={10}
                          y={optionalBaseY + (index * 16)}
                          style={{fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none', fill: '#6699cc'}}>
                        {this.formatDataProperty(prop)}
                    </text>
                ))}
                {/* Optional object property restrictions - in lighter orange */}
                {optionalObjectPropertyRestrictions.map((restriction, index) => (
                    <text key={`opt-obj-${index}`} className="property optional-axiom-property" x={10}
                          y={optionalBaseY + ((optionalOntologyProps.length + index) * 16)}
                          style={{fontFamily: 'Arial', fontSize: '12px', pointerEvents: 'none', fill: '#cc9966'}}>
                        {this.formatObjectPropertyRestriction(restriction)}
                    </text>
                ))}
                {/* Invisible event catcher - always receives events, even during transitions */}
                <rect className="event-catcher" width={width} height={totalHeight} rx={cornerRadius} ry={cornerRadius}
                      style={{fill: 'transparent', pointerEvents: 'all', cursor: 'pointer'}}/>
                {/* Tooltips - only shown when selected */}
                {this.props.isSelected && this.props.onAddRelationship && (
                    <Tooltips
                        diagramEditor={this.props.diagramEditor}
                        diagramClass={this.props.diagramClass}
                        width={width}
                        addRelationship={this.props.onAddRelationship}
                        onShowPicker={this.props.onShowRelationshipPicker}
                    />
                )}
            </g>
        );
    }

    handleDragStart = (event: d3.D3DragEvent<SVGGElement, unknown, unknown>) => {
        this.props.diagramEditor.handleClassDragStart(event, this)
    }

    handleDragged = (event: d3.D3DragEvent<SVGGElement, unknown, unknown>) => {
        this.props.diagramEditor.handleClassDragged(event, this);
    }

    handleDragEnd = (event: d3.D3DragEvent<SVGGElement, unknown, unknown>) => {
        this.props.diagramEditor.handleClassDragEnd(event, this);
    }

    componentDidMount() {
        d3.select(this.svgClass.current!)
            .on('mouseover', (event) => this.handleMouseOver(event))
            .on('mouseout', (event) => this.handleMouseOut(event))
            .on('click', (event) => this.handleMouseClick(event))
            .call(
                d3.drag<SVGGElement, unknown>()
                    .on('start', (event) => this.handleDragStart(event))
                    .on('drag', (event) => this.handleDragged(event))
                    .on('end', (event) => this.handleDragEnd(event)));

        // Apply initial selection state
        if (this.props.isSelected) {
            this.highlight(true);
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        // Handle isSelected prop changes
        if (prevProps.isSelected !== this.props.isSelected) {
            this.highlight(this.props.isSelected ?? false);
        }
        // Handle reconnection highlight changes
        if (prevProps.reconnectHighlight !== this.props.reconnectHighlight) {
            this.showReconnectHighlight(this.props.reconnectHighlight ?? false);
        }
    }

    getPosition() {
        return getTranslateValues(d3.select(this.svgClass.current)?.attr("transform"));
    }

    /**
     * Get the current width of the class element.
     * Reads from the rendered rect element.
     */
    getWidth(): number {
        const rect = d3.select(this.svgClass.current).select("rect.wc");
        if (!rect.empty()) {
            return parseFloat(rect.attr("width")) || 150;
        }
        return 150; // Default minimum width
    }

    /**
     * Get the appropriate title background color based on stereotype visibility and categoryColor.
     * Returns an opaque color with sufficient luminance for readable black text.
     */
    getTitleBackgroundColor(): string {
        const theme = getTheme();
        const showStereotype = this.props.showStereotype ?? true;
        const categoryColor = DiagramClassElement.getCategoryColor(this.props.diagramClass.modelElement, this.props.getClassColor);
        if (!showStereotype && categoryColor) {
            return ensureOpaqueReadableColor(categoryColor);
        }
        return theme.classTitleBackground;
    }

    highlight(enabled: boolean) {
        const theme = getTheme();
        const svgClass = d3.select(this.svgClass.current);

        // Interrupt any ongoing transitions before starting new ones
        svgClass.selectAll("*").interrupt();

        if (enabled) {
            svgClass.select("rect.wchover")
                .transition()
                .duration(300) // Duration of the animation in milliseconds
                .style("stroke-width", 10);
            svgClass.select("rect.title")
                .transition()
                .duration(300) // Duration of the animation in milliseconds
                .style("fill", theme.classTitleBackgroundHover);
            svgClass.select("rect.props")
                .transition()
                .duration(300) // Duration of the animation in milliseconds
                .style("fill", theme.classPropsBackgroundHover);
        } else {
            svgClass.select("rect.wchover")
                .transition()
                .duration(300)
                .style("stroke-width", 1);
            svgClass.select("rect.title")
                .transition()
                .duration(300) // Duration of the animation in milliseconds
                .style("fill", this.getTitleBackgroundColor());
            svgClass.select("rect.props")
                .transition()
                .duration(300) // Duration of the animation in milliseconds
                .style("fill", theme.classPropsBackground);
        }
    }

    /**
     * Show special highlighting when this class is a potential reconnection target.
     * Uses a distinct visual style (green glow) to differentiate from normal hover.
     */
    showReconnectHighlight(enabled: boolean) {
        const theme = getTheme();
        const svgClass = d3.select(this.svgClass.current);

        // Interrupt any ongoing transitions
        svgClass.selectAll("*").interrupt();

        if (enabled) {
            // Use a distinctive green/cyan color for reconnection targets
            svgClass.select("rect.wchover")
                .transition()
                .duration(150)
                .style("stroke-width", 12)
                .style("stroke", theme.anchorHoverStroke)
                .style("stroke-opacity", 0.8);
            svgClass.select("rect.title")
                .transition()
                .duration(150)
                .style("fill", theme.classTitleBackgroundHover);
            svgClass.select("rect.props")
                .transition()
                .duration(150)
                .style("fill", theme.classPropsBackgroundHover);
        } else {
            // Reset to normal appearance
            svgClass.select("rect.wchover")
                .transition()
                .duration(150)
                .style("stroke-width", 0)
                .style("stroke", theme.classBorderHover)
                .style("stroke-opacity", 0.5);
            svgClass.select("rect.title")
                .transition()
                .duration(150)
                .style("fill", this.getTitleBackgroundColor());
            svgClass.select("rect.props")
                .transition()
                .duration(150)
                .style("fill", theme.classPropsBackground);
        }
    }

    refresh() {

        d3.select(this.svgClass.current).selectAll("text.property").remove();
        d3.select(this.svgClass.current).selectAll("text.proposed").remove();
        const instanceProps = DiagramClassElement.getInstanceProperties(this.props.diagramClass.modelElement);
        instanceProps.forEach((prop, index) => {
            d3.select(this.svgClass.current).append("text")
                .attr("class", "property")
                .attr("x", 10)
                .attr("y", 40 + 4 + 12 + (index * 16))
                .text(prop)
                .style("font-family", "Arial")
                .style("font-size", "12px")
                .style("pointer-events", "none");

        });
    }
}

export {DiagramClassElement};